import { useState } from 'react';

interface Query {
  title: string;
  description: string;
  kql: string;
}

interface RelatedKQLQueriesProps {
  paygRate: number;
  sentinelEnabled: boolean;
}

const PORTAL_URL =
  'https://portal.azure.com/#blade/Microsoft_Azure_Monitoring_Logs/LogsBlade';

function buildQueries(paygRate: number, sentinelEnabled: boolean): Query[] {
  const queries: Query[] = [
    {
      title: 'Ingestion cost by table (last 30 days)',
      description: 'Shows estimated monthly cost per table based on PAYG rate',
      kql: `// Ingestion cost by table — last 30 days
// Set CostPerGB to your current PAYG rate (UK South)
let CostPerGB = ${paygRate};
let startTime = ago(30d);
Usage
| where TimeGenerated >= startTime
| where IsBillable == true
| summarize
    IngestedGB = sum(Quantity) / 1000,
    EstimatedCost = sum(Quantity) / 1000 * CostPerGB
    by DataType
| sort by EstimatedCost desc
| project
    Table = DataType,
    ['Ingested (GB)'] = round(IngestedGB, 3),
    ['Est. cost (£)'] = round(EstimatedCost, 2)`,
    },
    {
      title: 'Daily ingestion trend (last 30 days)',
      description: 'Track day-by-day ingestion volume to spot spikes',
      kql: `// Daily ingestion trend — last 30 days
let CostPerGB = ${paygRate};
Usage
| where TimeGenerated >= ago(30d)
| where IsBillable == true
| summarize
    DailyGB = sum(Quantity) / 1000
    by bin(TimeGenerated, 1d)
| extend DailyCost = DailyGB * CostPerGB
| sort by TimeGenerated asc
| project
    Date = format_datetime(TimeGenerated, 'yyyy-MM-dd'),
    ['GB ingested'] = round(DailyGB, 2),
    ['Cost (£)'] = round(DailyCost, 2)`,
    },
  ];

  if (sentinelEnabled) {
    queries.push({
      title: 'Sentinel data ingestion cost',
      description: 'Shows billable Sentinel data types and their estimated cost',
      kql: `// Sentinel billable ingestion — last 30 days
let CostPerGB = ${paygRate};
Usage
| where TimeGenerated >= ago(30d)
| where IsBillable == true
| where DataType in (
    "SecurityEvent", "CommonSecurityLog", "Syslog",
    "AzureActivity", "SigninLogs", "AuditLogs",
    "OfficeActivity", "SecurityAlert"
  )
| summarize
    IngestedGB = sum(Quantity) / 1000,
    EstimatedCost = sum(Quantity) / 1000 * CostPerGB
    by DataType
| sort by EstimatedCost desc
| project
    Table = DataType,
    ['Ingested (GB)'] = round(IngestedGB, 3),
    ['Est. cost (£)'] = round(EstimatedCost, 2)`,
    });
  }

  return queries;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1 text-[10px] text-outline hover:text-on-surface transition-colors px-2 py-1 border border-outline-variant/30 rounded-sm hover:border-primary"
    >
      <span className="material-symbols-outlined text-[13px]">
        {copied ? 'check' : 'content_copy'}
      </span>
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

export function RelatedKQLQueries({ paygRate, sentinelEnabled }: RelatedKQLQueriesProps) {
  const queries = buildQueries(paygRate, sentinelEnabled);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <span className="material-symbols-outlined text-[18px] text-primary">terminal</span>
        <h3 className="text-[12px] font-bold uppercase tracking-widest text-on-surface-variant">
          Verify this estimate in your workspace
        </h3>
      </div>
      <p className="text-[11px] text-outline leading-relaxed">
        These KQL queries are pre-configured with your current PAYG rate (£{paygRate.toFixed(3)}/GB).
        Run them in Azure Monitor to compare estimated vs actual costs.
      </p>

      <div className="flex flex-col gap-3">
        {queries.map((q) => (
          <div key={q.title} className="border border-outline-variant/30 rounded-sm overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-surface-container">
              <div>
                <p className="text-[11px] font-semibold text-on-surface">{q.title}</p>
                <p className="text-[10px] text-outline mt-0.5">{q.description}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <CopyButton text={q.kql} />
                <a
                  href={PORTAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-[10px] text-primary hover:underline"
                >
                  Run in Portal
                  <span className="material-symbols-outlined text-[12px]">open_in_new</span>
                </a>
              </div>
            </div>
            <pre className="px-3 py-2.5 text-[10px] font-mono bg-[#0a0e18] overflow-x-auto leading-relaxed whitespace-pre" style={{ color: '#a8b4c8' }}>
              {q.kql}
            </pre>
          </div>
        ))}
      </div>
    </div>
  );
}
